import React, { useState } from 'react';
import {
  CheckCircle2,
  Grid,
  X
} from 'lucide-react';
import { api } from '@/lib/api';

interface BulkMatrixIntakeProps {
  user: any;
  categories: Array<{ id: string; name: string }>;
  onSuccess: () => void;
  onCancel: () => void;
}

export function BulkMatrixIntake({ user, categories, onSuccess, onCancel }: BulkMatrixIntakeProps) {
  const [baseName, setBaseName] = useState('');
  const [category, setCategory] = useState(categories[0]?.name || 'Ethnic Wear');
  const [colors, setColors] = useState<string[]>(['Blue', 'Red']);
  const [sizes, setSizes] = useState<string[]>(['S', 'M', 'L']);

  const [matrixStock, setMatrixStock] = useState<Record<string, number>>({});
  const [matrixCost, setMatrixCost] = useState<Record<string, number>>({});
  const [matrixMrp, setMatrixMrp] = useState<Record<string, number>>({});
  const [matrixDiscount, setMatrixDiscount] = useState<Record<string, number>>({});
  const [matrixSell, setMatrixSell] = useState<Record<string, number>>({});
  const [matrixWholesale, setMatrixWholesale] = useState<Record<string, number>>({});

  const [batchDiscount, setBatchDiscount] = useState<number | ''>('');
  const [newColor, setNewColor] = useState('');
  const [newSize, setNewSize] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const addColor = () => {
    if (newColor && !colors.includes(newColor)) {
      setColors([...colors, newColor]);
      setNewColor('');
    }
  };

  const removeColor = (color: string) => {
    setColors(colors.filter(c => c !== color));
  };

  const addSize = () => {
    if (newSize && !sizes.includes(newSize)) {
      setSizes([...sizes, newSize]);
      setNewSize('');
    }
  };

  const removeSize = (size: string) => {
    setSizes(sizes.filter(s => s !== size));
  };

  // Global batch discount apply helper
  const handleBatchDiscountChange = (val: number | '') => {
    setBatchDiscount(val);
    if (val === '') return;

    // Apply discount rate across all current sizes and colors
    colors.forEach(color => {
      sizes.forEach(size => {
        const key = `${color}-${size}`;
        const mrp = matrixMrp[key] || 0;
        setMatrixDiscount(prev => ({ ...prev, [key]: val }));
        if (mrp > 0) {
          const selling = mrp * (1 - val / 100);
          setMatrixSell(prev => ({ ...prev, [key]: Math.round(selling * 100) / 100 }));
        }
      });
    });
  };

  const handleMatrixCellChange = (color: string, size: string, field: 'STOCK' | 'COST' | 'MRP' | 'DISCOUNT' | 'SELL' | 'WHOLESALE', val: number) => {
    const key = `${color}-${size}`;
    if (field === 'STOCK') {
      setMatrixStock(prev => ({ ...prev, [key]: val }));
    } else if (field === 'COST') {
      setMatrixCost(prev => ({ ...prev, [key]: val }));
    } else if (field === 'WHOLESALE') {
      setMatrixWholesale(prev => ({ ...prev, [key]: val }));
    } else if (field === 'MRP') {
      setMatrixMrp(prev => {
        const updated = { ...prev, [key]: val };
        // Check if there is an active discount percentage to auto-calculate Sell Price
        const disc = matrixDiscount[key] ?? (typeof batchDiscount === 'number' ? batchDiscount : 0);
        if (disc > 0) {
          const selling = val * (1 - disc / 100);
          setMatrixSell(s => ({ ...s, [key]: Math.round(selling * 100) / 100 }));
        }
        return updated;
      });
    } else if (field === 'DISCOUNT') {
      setMatrixDiscount(prev => ({ ...prev, [key]: val }));
      const mrp = matrixMrp[key] || 0;
      if (mrp > 0) {
        const selling = mrp * (1 - val / 100);
        setMatrixSell(prev => ({ ...prev, [key]: Math.round(selling * 100) / 100 }));
      }
    } else if (field === 'SELL') {
      setMatrixSell(prev => ({ ...prev, [key]: val }));
      const mrp = matrixMrp[key] || 0;
      if (mrp > 0) {
        const disc = ((mrp - val) / mrp) * 100;
        setMatrixDiscount(prev => ({ ...prev, [key]: Math.round(disc * 100) / 100 }));
      }
    }
  };

  const handleMatrixSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!baseName) {
      setErrorMessage('Base name is required.');
      return;
    }
    setIsSaving(true);
    setErrorMessage('');

    const payloadVariants: any[] = [];

    colors.forEach(color => {
      sizes.forEach(size => {
        const key = `${color}-${size}`;
        const stock = matrixStock[key] ?? 100;
        const cost = matrixCost[key] ?? 300;
        const mrp = matrixMrp[key] ?? 500;
        const disc = matrixDiscount[key] ?? (typeof batchDiscount === 'number' ? batchDiscount : 0.0);
        const sell = matrixSell[key] ?? mrp * (1 - disc / 100);
        const wholesale = matrixWholesale[key] ?? sell * 0.9; // Default wholesale to 10% below retail

        // Generate dynamic SKU
        const slugName = baseName.substring(0, 3).toUpperCase();
        const slugColor = color.substring(0, 2).toUpperCase();
        const sku = `${slugName}-${slugColor}-${size}`;

        payloadVariants.push({
          sku,
          color,
          size,
          currentStock: stock,
          costPrice: cost,
          mrp,
          discount_percentage: disc,
          retail_price: sell,
          wholesale_price: wholesale
        });
      });
    });

    try {
      const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
      const res = await api.post('/inventory/bulk-intake', {
        name: baseName,
        categoryName: category,
        variants: payloadVariants
      }, { headers });

      if (res.data.success) {
        onSuccess();
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to submit bulk intake matrices.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleMatrixSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Variation Config (Left 1/3) */}
      <div className="space-y-6">
        {/* Base Product config */}
        <div className="card bg-white border border-slate-150 p-5 rounded-xl shadow-sm">
          <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
            1. Base Product Template
          </h3>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Product Label / Name</label>
              <input
                type="text"
                required
                placeholder="e.g. Linen Chambray"
                value={baseName}
                onChange={(e) => setBaseName(e.target.value)}
                className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Category Group</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-medium"
              >
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Colors variations */}
        <div className="card bg-white border border-slate-155 p-5 rounded-xl shadow-sm">
          <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2 flex items-center justify-between">
            <span>2. Variations: Colors</span>
            <span className="text-[10px] text-slate-400 font-mono font-medium">{colors.length} loaded</span>
          </h3>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              placeholder="Add color..."
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="flex-1 px-2.5 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600"
            />
            <button
              type="button"
              onClick={addColor}
              className="px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded hover:bg-emerald-700 active:scale-95 cursor-pointer border border-emerald-500"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {colors.map(color => (
              <span key={color} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-605 rounded text-[10px] font-bold">
                <span>{color}</span>
                <button type="button" onClick={() => removeColor(color)} className="text-slate-400 hover:text-red-500 cursor-pointer">
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Sizes variations */}
        <div className="card bg-white border border-slate-155 p-5 rounded-xl shadow-sm">
          <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2 flex items-center justify-between">
            <span>3. Variations: Sizes</span>
            <span className="text-[10px] text-slate-400 font-mono font-medium">{sizes.length} loaded</span>
          </h3>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              placeholder="Add size..."
              value={newSize}
              onChange={(e) => setNewSize(e.target.value)}
              className="flex-1 px-2.5 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600"
            />
            <button
              type="button"
              onClick={addSize}
              className="px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded hover:bg-emerald-700 active:scale-95 cursor-pointer border border-emerald-500"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {sizes.map(size => (
              <span key={size} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-655 rounded text-[10px] font-bold">
                <span>{size}</span>
                <button type="button" onClick={() => removeSize(size)} className="text-slate-400 hover:text-red-500 cursor-pointer">
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Matrix Intake Grid (Right 2/3) */}
      <div className="lg:col-span-2 space-y-6">
        <div className="card bg-white border border-slate-150 p-5 rounded-xl shadow-sm">
          <div className="border-b border-slate-100 pb-3 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">
                4. Variation Matrix Grid
              </h3>
              <p className="text-[10px] text-slate-400 mt-0.5">Real-time discount calculating system.</p>
            </div>

            {/* Global Batch Discount Setup */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg">
              <label className="text-[9px] font-extrabold text-slate-500 uppercase whitespace-nowrap">Batch Discount %</label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="0"
                value={batchDiscount}
                onChange={(e) => handleBatchDiscountChange(e.target.value === '' ? '' : Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                className="w-12 px-1.5 py-0.5 border border-slate-250 rounded text-[10px] text-center focus:outline-none focus:border-emerald-605 font-bold"
              />
            </div>
          </div>

          {errorMessage && (
            <div className="mb-4 text-xs font-bold text-red-500 bg-red-50 border border-red-200 p-2.5 rounded-lg">
              {errorMessage}
            </div>
          )}

          {colors.length === 0 || sizes.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Grid size={40} className="mx-auto mb-2 opacity-30 text-emerald-600" />
              <p className="text-xs font-semibold">Grid could not be constructed.</p>
              <p className="text-[10px] text-slate-400 mt-1">Please ensure you have loaded at least one color and size.</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[9px] border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 font-bold uppercase text-[8px] text-slate-600">
                      <th className="py-2 px-2.5" colSpan={2}>Matrix (Size)</th>
                      {colors.map(color => (
                        <th key={color} className="py-2 px-2 text-center border-l border-slate-200 bg-slate-100/50" colSpan={6}>
                          {color}
                        </th>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-200 bg-slate-100 text-[8px] font-bold text-slate-500 text-center">
                      <th className="py-1 px-2.5 text-left w-10">S.No.</th>
                      <th className="py-1 px-2.5 text-left">Size</th>
                      {colors.map(color => (
                        <React.Fragment key={`${color}-headers`}>
                          <th className="py-1 px-0.5 border-l border-slate-200">Stock</th>
                          <th className="py-1 px-0.5">Cost</th>
                          <th className="py-1 px-0.5 bg-amber-50/50">MRP (₹)</th>
                          <th className="py-1 px-0.5 bg-amber-50/50">Disc %</th>
                          <th className="py-1 px-0.5 bg-emerald-50/50">Sell (₹)</th>
                          <th className="py-1 px-0.5">Whls</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sizes.map((size, idx) => (
                      <tr key={size} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-2 px-2.5 font-semibold text-slate-500 bg-slate-50 text-center">{idx + 1}</td>
                        <td className="py-2 px-2.5 font-bold text-slate-800 bg-slate-50">{size}</td>
                        {colors.map(color => {
                          const key = `${color}-${size}`;
                          return (
                            <React.Fragment key={`${color}-${size}-cells`}>
                              {/* Stock */}
                              <td className="p-1 border-l border-slate-150">
                                <input
                                  type="number"
                                  placeholder="100"
                                  value={matrixStock[key] ?? ''}
                                  onChange={(e) => handleMatrixCellChange(color, size, 'STOCK', parseInt(e.target.value) || 0)}
                                  className="w-10 px-1 py-0.5 border border-slate-200 rounded text-center font-mono"
                                />
                              </td>
                              {/* Cost */}
                              <td className="p-1">
                                <input
                                  type="number"
                                  placeholder="300"
                                  value={matrixCost[key] ?? ''}
                                  onChange={(e) => handleMatrixCellChange(color, size, 'COST', parseInt(e.target.value) || 0)}
                                  className="w-10 px-1 py-0.5 border border-slate-200 rounded text-center font-mono"
                                />
                              </td>
                              {/* MRP */}
                              <td className="p-1 bg-amber-50/20">
                                <input
                                  type="number"
                                  placeholder="500"
                                  value={matrixMrp[key] ?? ''}
                                  onChange={(e) => handleMatrixCellChange(color, size, 'MRP', parseFloat(e.target.value) || 0)}
                                  className="w-11 px-1 py-0.5 border border-amber-250 rounded text-center font-mono font-bold text-amber-800"
                                />
                              </td>
                              {/* Discount % */}
                              <td className="p-1 bg-amber-50/20">
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={matrixDiscount[key] ?? ''}
                                  onChange={(e) => handleMatrixCellChange(color, size, 'DISCOUNT', parseFloat(e.target.value) || 0)}
                                  className="w-9 px-1 py-0.5 border border-amber-250 rounded text-center font-mono text-amber-900"
                                />
                              </td>
                              {/* Selling Price */}
                              <td className="p-1 bg-emerald-50/20">
                                <input
                                  type="number"
                                  placeholder="500"
                                  value={matrixSell[key] ?? ''}
                                  onChange={(e) => handleMatrixCellChange(color, size, 'SELL', parseFloat(e.target.value) || 0)}
                                  className="w-11 px-1 py-0.5 border border-emerald-250 rounded text-center font-mono font-bold text-emerald-800"
                                />
                              </td>
                              {/* Wholesale Price */}
                              <td className="p-1">
                                <input
                                  type="number"
                                  placeholder="450"
                                  value={matrixWholesale[key] ?? ''}
                                  onChange={(e) => handleMatrixCellChange(color, size, 'WHOLESALE', parseFloat(e.target.value) || 0)}
                                  className="w-10 px-1 py-0.5 border border-slate-200 rounded text-center font-mono"
                                />
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 bg-white border border-slate-200 text-xs font-semibold text-slate-650 rounded hover:bg-slate-100 active:scale-95 cursor-pointer shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !baseName}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold rounded hover:shadow active:scale-95 cursor-pointer flex items-center gap-1.5 border border-emerald-500"
                >
                  <CheckCircle2 size={13} />
                  <span>{isSaving ? 'Submitting...' : 'Commit Variant Matrix'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
